import React, { useRef, useLayoutEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import Grid from 'apollo-react/components/Grid';
import PasswordField from '../../../../components/common/Form/passwordField';
import ButtonCommon from '../../../../components/common/Button';
import {
  verifyMyPasswordSchema,
  changeMyPasswordSchema,
} from '../../../../validation/user';
import styles from '../Profile.module.css';
import { hcpProfileFormFieldVariant } from '../../../../components/HcpSignup/hcpSignupDefaults';

const PASSWORD_VERIFY_STACK_MAX_PX = 576;

function measurePasswordVerifyButtonPaddingTop(rowEl, buttonMeasureEl) {
  if (!rowEl || typeof rowEl.getBoundingClientRect !== 'function') return 0;
  const input = rowEl.querySelector('.MuiInputBase-root');
  if (!input) return 0;
  const rowTop = rowEl.getBoundingClientRect().top;
  const inputRect = input.getBoundingClientRect();
  const btnH =
    buttonMeasureEl &&
    typeof buttonMeasureEl.getBoundingClientRect === 'function'
      ? buttonMeasureEl.getBoundingClientRect().height
      : 40;
  const centered =
    inputRect.top - rowTop + Math.max(0, (inputRect.height - btnH) / 2);
  return Math.max(0, Math.round(centered));
}

/**
 * Apollo `TextField` (standard + shrunk label) does not match a fixed CSS offset. Measure
 * `.MuiInputBase-root` vs the row so Verify lines up with the password input with or without helper text.
 */
function CurrentPasswordVerifyRow({
  profileStyles,
  values,
  errors,
  setFieldValue,
  setFieldError,
  onVerify,
  t,
  fieldVariant,
}) {
  const rowRef = useRef(null);
  const fieldRef = useRef(null);
  const buttonMeasureRef = useRef(null);
  const [buttonPadTop, setButtonPadTop] = useState(0);

  const sync = useCallback(() => {
    if (
      typeof window !== 'undefined' &&
      window.innerWidth <= PASSWORD_VERIFY_STACK_MAX_PX
    ) {
      setButtonPadTop(0);
      return;
    }
    setButtonPadTop(
      measurePasswordVerifyButtonPaddingTop(
        rowRef.current,
        buttonMeasureRef.current
      )
    );
  }, []);

  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(sync);
    });
    return () => cancelAnimationFrame(raf);
  }, [sync, errors?.currentPassword, values.currentPassword]);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const field = fieldRef.current;
    const btnWrap = buttonMeasureRef.current;
    if (!row) return undefined;
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => sync());
      ro.observe(row);
      if (field) ro.observe(field);
      if (btnWrap) ro.observe(btnWrap);
    }
    window.addEventListener('resize', sync);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [sync]);

  return (
    <div ref={rowRef} className={profileStyles.passwordVerifyRow}>
      <div ref={fieldRef} className={profileStyles.passwordVerifyField}>
        <Field name="currentPassword">
          {({ field, meta }) => (
            <PasswordField
              field={field}
              meta={meta}
              margin="none"
              variant={fieldVariant}
              label={t('profileData.currentPassword', {
                defaultValue: 'Current password',
              })}
              value={values.currentPassword}
              onChange={(v) => setFieldValue('currentPassword', v)}
            />
          )}
        </Field>
      </div>
      <div
        className={profileStyles.passwordVerifyButtonWrap}
        style={{ paddingTop: buttonPadTop }}
      >
        <span
          ref={buttonMeasureRef}
          className={profileStyles.passwordVerifyButtonInner}
        >
          <ButtonCommon
            color="primary"
            className={`${profileStyles.btnSized} ${profileStyles.passwordVerifyButton}`}
            onClick={() => onVerify(values, setFieldError)}
            text={t('profileData.verify', { defaultValue: 'Verify' })}
          />
        </span>
      </div>
    </div>
  );
}

const PasswordSection = ({
  verified,
  requireCurrentPassword,
  onVerifyPassword,
  onChangePassword,
  onCancel,
}) => {
  const { t } = useTranslation();
  const passwordSchema = toFormikValidationSchema(
    changeMyPasswordSchema(t, requireCurrentPassword)
  );

  const handleVerifyClick = (values, setFieldError) => {
    const result = verifyMyPasswordSchema(t).safeParse({
      currentPassword: values.currentPassword,
    });
    if (!result.success) {
      setFieldError('currentPassword', result.error.issues[0].message);
      return;
    }
    onVerifyPassword(values.currentPassword);
  };

  return (
    <Formik
      initialValues={{
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }}
      validationSchema={passwordSchema}
      onSubmit={async (values) => {
        await onChangePassword(values.currentPassword, values.newPassword);
      }}
    >
      {({
        values,
        errors,
        setFieldValue,
        handleSubmit,
        setFieldError,
        resetForm,
      }) => (
        <Form onSubmit={handleSubmit}>
          <Grid
            container
            rowSpacing={0}
            columnSpacing={2}
            className={`w-100 m-0 ${styles.wizardFormGrid}`}
          >
            {requireCurrentPassword && (
              <Grid item xs={12}>
                <CurrentPasswordVerifyRow
                  profileStyles={styles}
                  values={values}
                  errors={errors}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  onVerify={handleVerifyClick}
                  t={t}
                  fieldVariant={hcpProfileFormFieldVariant}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <div
                className={
                  requireCurrentPassword && !verified ? styles.opacityDim : ''
                }
              >
                <Field name="newPassword">
                  {({ field, meta }) => (
                    <PasswordField
                      field={field}
                      meta={meta}
                      margin="none"
                      variant={hcpProfileFormFieldVariant}
                      label={t('profileData.newPassword', {
                        defaultValue: 'New password',
                      })}
                      value={values.newPassword}
                      onChange={(v) => setFieldValue('newPassword', v)}
                      disabled={requireCurrentPassword ? !verified : false}
                    />
                  )}
                </Field>
              </div>
            </Grid>
            <Grid item xs={12}>
              <div
                className={
                  requireCurrentPassword && !verified ? styles.opacityDim : ''
                }
              >
                <Field name="confirmPassword">
                  {({ field, meta }) => (
                    <PasswordField
                      field={field}
                      meta={meta}
                      margin="none"
                      variant={hcpProfileFormFieldVariant}
                      label={t('profileData.confirmPassword', {
                        defaultValue: 'Confirm password',
                      })}
                      value={values.confirmPassword}
                      onChange={(v) => setFieldValue('confirmPassword', v)}
                      disabled={requireCurrentPassword ? !verified : false}
                    />
                  )}
                </Field>
              </div>
            </Grid>
            <Grid item xs={12}>
              <div
                className={styles.actionsRightNoMargin}
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginTop: 4,
                }}
              >
                <ButtonCommon
                  color="secondary"
                  status="danger"
                  type="button"
                  className={styles.btnSized}
                  onClick={() => {
                    resetForm();
                    onCancel?.();
                  }}
                  text={t('Cancel', { defaultValue: 'Cancel' })}
                />
                <ButtonCommon
                  color="primary"
                  className={styles.btnSized}
                  type="submit"
                  disabled={
                    requireCurrentPassword
                      ? !verified ||
                        !values.newPassword ||
                        !values.confirmPassword
                      : !values.newPassword || !values.confirmPassword
                  }
                  text={t('Save', { defaultValue: 'Save' })}
                />
              </div>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default PasswordSection;
